import type { AdController } from "../types";

export function getAdAudioVolume(adPlayer: AdController): number {
  return adPlayer.getOriginalMutedState() ? 0 : adPlayer.getOriginalVolume();
}

export function toggleMute(
  video: HTMLVideoElement,
  adPlayer: AdController,
  debug: boolean
): void {
  if (adPlayer.isAdPlaying()) {
    const newMutedState = !adPlayer.getOriginalMutedState();
    const restoreVolume = adPlayer.getOriginalVolume() || 1;

    adPlayer.updateOriginalMutedState(newMutedState, video.volume);
    adPlayer.setAdVolume(newMutedState ? 0 : restoreVolume);

    if (debug) {
      console.log(
        "[StormcloudVideoPlayer] Mute toggle during ad - immediately applied:",
        newMutedState
      );
    }
  } else {
    video.muted = !video.muted;
    adPlayer.updateOriginalMutedState(video.muted, video.volume);
    if (debug) {
      console.log("[StormcloudVideoPlayer] Muted:", video.muted);
    }
  }
}

export function toggleFullscreen(
  video: HTMLVideoElement,
  debug: boolean
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!document.fullscreenElement) {
      const container = video.parentElement;
      if (!container) {
        reject(new Error("No parent container found for fullscreen"));
        return;
      }
      container
        .requestFullscreen()
        .then(() => {
          if (debug) {
            console.log("[StormcloudVideoPlayer] Entered fullscreen");
          }
          resolve();
        })
        .catch((err) => {
          if (debug) {
            console.error("[StormcloudVideoPlayer] Fullscreen error:", err);
          }
          reject(err);
        });
    } else {
      document
        .exitFullscreen()
        .then(() => {
          if (debug) {
            console.log("[StormcloudVideoPlayer] Exited fullscreen");
          }
          resolve();
        })
        .catch((err) => {
          if (debug) {
            console.error(
              "[StormcloudVideoPlayer] Exit fullscreen error:",
              err
            );
          }
          reject(err);
        });
    }
  });
}

export function isMuted(
  video: HTMLVideoElement,
  adPlayer: AdController
): boolean {
  if (adPlayer.isAdPlaying()) {
    return adPlayer.getOriginalMutedState();
  }
  return video.muted;
}

export function setMuted(
  video: HTMLVideoElement,
  adPlayer: AdController,
  muted: boolean,
  debug: boolean
): void {
  const adPlaying = adPlayer.isAdPlaying();

  if (adPlaying && muted === video.muted) {
    if (debug) {
      console.log(
        "[StormcloudVideoPlayer] setMuted reflective update during ad ignored",
        { muted }
      );
    }
    return;
  }

  video.muted = muted;

  if (adPlaying) {
    adPlayer.updateOriginalMutedState(muted, video.volume);
    adPlayer.setAdVolume(muted ? 0 : 1);
    if (debug) {
      console.log("[StormcloudVideoPlayer] setMuted applied during ad", {
        muted,
      });
    }
    return;
  }

  adPlayer.updateOriginalMutedState(muted, video.volume);
  if (debug) {
    console.log("[StormcloudVideoPlayer] setMuted called:", muted);
  }
}

export function setVolume(
  video: HTMLVideoElement,
  adPlayer: AdController,
  volume: number,
  debug: boolean
): void {
  const clampedVolume = Math.max(0, Math.min(1, volume));
  const adPlaying = adPlayer.isAdPlaying();

  if (adPlaying) {
    adPlayer.setAdVolume(clampedVolume);
    adPlayer.updateOriginalMutedState(clampedVolume === 0, clampedVolume);
    if (debug) {
      console.log("[StormcloudVideoPlayer] setVolume applied during ad", {
        volume: clampedVolume,
      });
    }
  } else {
    video.volume = clampedVolume;
    video.muted = clampedVolume === 0;
    adPlayer.updateOriginalMutedState(clampedVolume === 0, clampedVolume);
    if (debug) {
      console.log("[StormcloudVideoPlayer] setVolume called:", clampedVolume);
    }
  }
}

export function getVolume(
  video: HTMLVideoElement,
  adPlayer: AdController
): number {
  const adPlaying = adPlayer.isAdPlaying();
  if (adPlaying) {
    return adPlayer.getAdVolume();
  }
  return video.volume;
}

export function isFullscreen(): boolean {
  return !!document.fullscreenElement;
}

export function resizePlayer(
  video: HTMLVideoElement,
  adPlayer: AdController,
  debug: boolean
): void {
  if (debug) {
    console.log("[StormcloudVideoPlayer] Resizing player");
  }

  if (adPlayer && adPlayer.isAdPlaying()) {
    const width = video.clientWidth || 640;
    const height = video.clientHeight || 480;

    if (debug) {
      console.log(
        `[StormcloudVideoPlayer] Resizing ads manager to ${width}x${height}`
      );
    }

    adPlayer.resize(width, height);
  }
}
